import { useQuery } from '@tanstack/react-query'
import { 
  TrendingUp, 
  Wallet, 
  Target, 
  Activity,
  ArrowUpRight,
  ArrowDownRight 
} from 'lucide-react'
import { analyticsApi } from '../lib/api'
import { formatCurrency, formatPercent } from '../lib/utils'
import { useAuthStore } from '../store/authStore'

export function Dashboard() {
  const { isHydrated } = useAuthStore()
  
  const { data: overview, isLoading } = useQuery({
    queryKey: ['overview'],
    queryFn: () => analyticsApi.getOverview().then(r => r.data),
    enabled: isHydrated, // Only run after auth store is hydrated
  })

  // Ensure arrays exist to prevent .filter()/.map() errors
  const safeOverview = {
    stats: overview?.stats || { totalWallets: 0, totalMarkets: 0, totalVolume: 0 },
    topWallets: overview?.topWallets || [],
    opportunities: overview?.opportunities || [],
  }

  if (isLoading) {
    return <div className="text-center py-12">Loading...</div>
  }

  const stats = [
    {
      name: 'Total Wallets Tracked',
      value: safeOverview.stats.totalWallets || 0,
      icon: Wallet,
      change: '+12%',
      changeType: 'positive' as const,
    },
    {
      name: 'Active Markets',
      value: safeOverview.stats.totalMarkets || 0,
      icon: Target,
      change: '+5%',
      changeType: 'positive' as const,
    },
    {
      name: 'Total Volume',
      value: formatCurrency(safeOverview.stats.totalVolume || 0),
      icon: TrendingUp,
      change: '+23%',
      changeType: 'positive' as const,
    },
    {
      name: 'Trading Volume (24h)',
      value: '$2.1B',
      icon: Activity,
      change: '+8%',
      changeType: 'positive' as const,
    },
  ]

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <stat.icon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              {stat.changeType === 'positive' ? (
                <ArrowUpRight className="w-4 h-4 text-green-600 mr-1" />
              ) : (
                <ArrowDownRight className="w-4 h-4 text-red-600 mr-1" />
              )}
              <span className={stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'}>
                {stat.change}
              </span>
              <span className="text-gray-500 ml-2">from last week</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Wallets */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Top Performing Wallets</h2>
            <p className="mt-1 text-sm text-gray-500">Based on Sharpe ratio and win rate</p>
          </div>
          
          <div className="divide-y divide-gray-200">
            {safeOverview.topWallets?.map((wallet: any) => (
              <div key={wallet.id} className="p-6 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{wallet.displayName}</p>
                  <p className="text-sm text-gray-500">{wallet.totalTrades} trades</p>
                </div>
                
                <div className="text-right">
                  <p className="font-medium text-green-600">+{formatCurrency(wallet.totalPnL)}</p>
                  <p className="text-sm text-gray-500">
                    Sharpe: {wallet.sharpeRatio?.toFixed(2) || '0.00'} | Win: {formatPercent(wallet.winRate)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active Opportunities */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Trading Opportunities</h2>
            <p className="mt-1 text-sm text-gray-500">AI-detected positive EV opportunities</p>
          </div>
          
          <div className="divide-y divide-gray-200">
            {safeOverview.opportunities?.map((opp: any) => (
              <div key={opp.id} className="p-6">
                <p className="font-medium text-gray-900 line-clamp-2">{opp.market?.question || 'Unknown Market'}</p>
                
                <div className="mt-2 flex items-center space-x-4">
                  <span className="text-sm text-green-600 font-medium">
                    +{((opp.edge || 0) * 100).toFixed(1)}% edge
                  </span>
                  <span className="text-sm text-gray-500">
                    EV: {((opp.expectedValue || 0) * 100).toFixed(1)}%
                  </span>
                  <span className="text-sm text-gray-500">
                    Confidence: {((opp.confidence || 0) * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            ))}
            
            {(!safeOverview.opportunities || safeOverview.opportunities.length === 0) && (
              <div className="p-6 text-center text-gray-500">
                No active opportunities. Check back soon!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
